/**
 * Global error capture.
 *
 * @module logging/instrumentation/errors
 */

import { classifyError, describeError } from '../core/classify';
import { telemetry } from '../telemetry';

/**
 * Emit `web_error` for uncaught errors and unhandled rejections.
 *
 * ## Why this does not call `reportError`
 *
 * `initErrorReporting()` starts the Stackdriver client with its defaults,
 * `reportUncaughtExceptions` and `reportUnhandledPromiseRejections`, both
 * of which are true. It has therefore already hooked `window.onerror` and
 * `window.onunhandledrejection` and is reporting every uncaught error with
 * its own stack extraction, which is better than anything reconstructable
 * from an `ErrorEvent`.
 *
 * The previous implementation registered these same handlers *and* called
 * `reportError` from inside them, so every uncaught error reached Cloud
 * Error Reporting twice — inflating occurrence counts on exactly the errors
 * anyone would look at first. This module emits the countable event and
 * leaves the stack to the client that already has it.
 *
 * Caught errors are the opposite case: nothing else sees them, so
 * `logError()` reports those to both sinks.
 */
export function initErrorInstrumentation(): void {
  window.addEventListener('error', (event: ErrorEvent) => {
    const message = event.message || describeError(event.error);
    telemetry.emit(
      'web_error',
      { error_category: classifyError(message), message },
      { dedupeKey: message },
    );
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const message = describeError(event.reason);
    telemetry.emit(
      'web_error',
      { error_category: classifyError(message), message },
      { dedupeKey: message },
    );
  });
}
